import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Collapse from "@mui/material/Collapse";
import TopNavBar from "../components/TopNavBar";
import DeviceCard from "../components/DeviceCard";
import { getDevices } from "../api/devices";
import { getCustomers } from "../api/customer";
import LaunchIcon from "@mui/icons-material/Launch";
import { useAuth } from "../context/AuthContext";

export default function CustomersPage() {
  const { user } = useAuth();
  const username = user?.username;
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [rcDevices, setRcDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const fetchPageData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [devicesResponse, customersData] = await Promise.all([
        getDevices(true),
        getCustomers(),
      ]);

      const payload = Array.isArray(devicesResponse)
        ? { devices: devicesResponse, rc_devices: [] }
        : devicesResponse || { devices: [], rc_devices: [] };

      const deviceList = Array.isArray(payload.devices) ? payload.devices : [];
      const rcDeviceList = Array.isArray(payload.rc_devices)
        ? payload.rc_devices
        : [];

      setDevices(deviceList);
      setRcDevices(rcDeviceList);
      setCustomers(customersData);
      console.log("Fetched devices:", deviceList);
      console.log("Fetched rc-devices:", rcDeviceList);
    } catch (err) {
      console.error("Error loading page data:", err.message);
      setError("Failed to load customers and devices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  // Build a map of customer_pk_id -> devices and ensure every customer is present
  const devicesByCustomer = React.useMemo(() => {
    const map = {};
    const getCustomerId = (item) => item.customer_pk_id ?? item.customer_id;

    customers.forEach((c) => {
      map[c.pk_id] = [];
    });

    [...devices, ...rcDevices].forEach((item) => {
      const cid = getCustomerId(item);
      if (cid && map[cid]) {
        map[cid].push(item);
      } else {
        map.unassigned = map.unassigned || [];
        map.unassigned.push(item);
      }
    });

    return map;
  }, [customers, devices, rcDevices]);

  // Sort customers alphabetically by display name
  const sortedCustomers = React.useMemo(() => {
    return [...customers].sort((a, b) => {
      const nameA = (a.uk_name || a.name || "").toLowerCase();
      const nameB = (b.uk_name || b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [customers]);

  // Initialize collapsed state for customers when customers load
  useEffect(() => {
    if (customers.length > 0) {
      const init = {};
      customers.forEach((c) => {
        init[c.pk_id] = false; // default collapsed = false (open)
      });
      setCollapsed((prev) => ({ ...init, ...prev }));
    }
  }, [customers]);

  // Filter function for search (matches customer name or device name/id)
  const filteredCustomers = sortedCustomers.filter((customer) => {
    const displayName = customer.uk_name || customer.name || "Unknown";
    const customerMatches = displayName
      .toLowerCase()
      .includes(search.toLowerCase());

    const customerDevices = devicesByCustomer[customer.pk_id] || [];
    const robotMatches = customerDevices.some(
      (device) =>
        (device.name &&
          device.name.toLowerCase().includes(search.toLowerCase())) ||
        (device.uk_device_id &&
          device.uk_device_id.toLowerCase().includes(search.toLowerCase())) ||
        (device.device_id &&
          device.device_id.toLowerCase().includes(search.toLowerCase())),
    );

    return customerMatches || robotMatches;
  });

  const handleToggle = (customerPk) => {
    setCollapsed((prev) => ({
      ...prev,
      [customerPk]: !prev[customerPk],
    }));
  };

  return (
    <>
      <TopNavBar
        title="Robot Cleaner Dashboard"
        username={username}
        heading={"Customers"}
      />

      <Container sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Welcome, {username}!
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <TextField
            label="Search customers or robots"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSearch("")}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 2, whiteSpace: "nowrap" }}
            onClick={() => navigate("/create-customer")}
          >
            Create New
          </Button>
        </Box>

        {loading && <CircularProgress />}
        {error && <Typography color="error">{error}</Typography>}

        {/* Render customers and their devices */}
        {filteredCustomers.map((customer) => {
          const customerDevices = devicesByCustomer[customer.pk_id] || [];
          const displayName = customer.uk_name || customer.name || "Unknown";
          const isOpen = collapsed[customer.pk_id] ?? true;

          return (
            <Box key={customer.pk_id} sx={{ mb: 6 }}>
              <Paper
                elevation={customerDevices.length > 0 ? 1 : 3}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor:
                    customerDevices.length > 0 ? "inherit" : "#ffebee",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleToggle(customer.pk_id)}
              >
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {displayName} ({customerDevices.length}{" "}
                  {customerDevices.length === 1 ? "device" : "devices"})
                </Typography>
                <IconButton
                  aria-label="View customer"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/customer/${customer.pk_id}`);
                  }}
                  sx={{ ml: 1 }}
                >
                  <LaunchIcon />
                </IconButton>
                {isOpen ? (
                  <ExpandLessIcon
                    sx={{
                      transition: "transform 0.2s",
                      transform: "rotate(0deg)",
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{
                      transition: "transform 0.2s",
                      transform: "rotate(0deg)",
                    }}
                  />
                )}
              </Paper>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Grid container spacing={2}>
                  {customerDevices.map((device, index) => (
                    <Grid
                      key={device.device_id || device.pk_id || index}
                      item
                      size={{ xs: 12, sm: 6, md: 4 }}
                    >
                      <DeviceCard
                        device={device}
                        size={"small"}
                        disabled={false}
                      />
                    </Grid>
                  ))}

                  {/* show placeholder if no devices */}
                  {customerDevices.length === 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography>No devices for this customer.</Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Collapse>
            </Box>
          );
        })}

        {/* Optionally render unassigned devices */}
        {devicesByCustomer.unassigned &&
          devicesByCustomer.unassigned.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">
                  Unassigned Devices ({devicesByCustomer.unassigned.length})
                </Typography>
              </Paper>
              <Grid container spacing={2} justifyContent={"center"}>
                {devicesByCustomer.unassigned.map((device, idx) => (
                  <Grid
                    key={device.device_id || device.pk_id || idx}
                    item
                    size={{ xs: 12, sm: 6, md: 4 }}
                  >
                    <DeviceCard device={device} size={"small"} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
      </Container>
    </>
  );
}
